'use client'

import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import type { AuthIdentity, AuthSession, AuthUser } from '@/lib/api-types'

type AuthStatus = 'loading' | 'authenticated' | 'guest'

interface AuthStore {
  user: AuthUser | null
  identity: AuthIdentity | null
  session: AuthSession | null
  status: AuthStatus
  hasHydratedFromStorage: boolean
  isVerifyingSession?: boolean
  setAuthenticated: (payload: {
    user: AuthUser
    identity: AuthIdentity
    session: AuthSession | null
  }) => void
  setGuest: () => void
}

type PersistedAuthState = Pick<AuthStore, 'status' | 'user' | 'identity' | 'session'>

type PersistApi = {
  hasHydrated: () => boolean
  rehydrate: () => Promise<void>
}

export const useAuthStore = create<AuthStore>()(
  persist<AuthStore, [], [], PersistedAuthState>(
    (set) => ({
      user: null,
      identity: null,
      session: null,
      status: 'loading',
      hasHydratedFromStorage: false,
      isVerifyingSession: false,
      setAuthenticated: ({ user, identity, session }) =>
        set({
          user,
          identity,
          session,
          status: 'authenticated',
          isVerifyingSession: false,
        }),
      setGuest: () =>
        set({
          user: null,
          identity: null,
          session: null,
          status: 'guest',
          isVerifyingSession: false,
        }),
    }),
    {
      name: 'auth-store',
      storage: createJSONStorage(() => localStorage),
      partialize: ({ status, user, identity, session }) => ({
        status,
        user,
        identity,
        session,
      }),
      onRehydrateStorage: () => {
        return () => {
          useAuthStore.setState({ hasHydratedFromStorage: true })
        }
      },
    },
  ),
)

let hydrationPromise: Promise<void> | null = null

function getPersistApi() {
  return (useAuthStore as typeof useAuthStore & { persist?: PersistApi }).persist ?? null
}

export async function ensureAuthStoreHydrated() {
  const persistApi = getPersistApi()

  if (!persistApi) {
    useAuthStore.setState({ hasHydratedFromStorage: true })
    return
  }

  if (persistApi.hasHydrated()) {
    if (!useAuthStore.getState().hasHydratedFromStorage) {
      useAuthStore.setState({ hasHydratedFromStorage: true })
    }
    return
  }

  if (hydrationPromise) {
    return hydrationPromise
  }

  hydrationPromise = (async () => {
    try {
      await persistApi.rehydrate()
    } catch {
      // If storage rehydration fails, unblock the UI and let bootstrap
      // continue from a clean guest state instead of hanging on loading.
      useAuthStore.getState().setGuest()
    } finally {
      if (!useAuthStore.getState().hasHydratedFromStorage) {
        useAuthStore.setState({ hasHydratedFromStorage: true })
      }
      hydrationPromise = null
    }
  })()

  return hydrationPromise
}
